// Reconciliación del Kanban activo — red de seguridad para el push WS perdido.
//
// Problema (CS-139/CS-140, comercio LA CURADERÍA SOCIETY, 06-21): el backend activó un pedido
// programado (`scheduled → pending`) y emitió `newOrder` por WebSocket a una conexión viva, sin
// errores, pero el pedido NUNCA se materializó en el Kanban. El push es fire-and-forget (sin
// replay): si el socket estaba caído/idle/stale al dispararse el `auto_fire` (hora arbitraria), o
// el GET de `onNewOrder` ganó la carrera y aún leyó el pedido como `scheduled`, el pedido se pierde
// del Kanban hasta recargar la página.
//
// La reconciliación re-trae los pedidos activos (`GET /v1/pedido?orders_in_process=true`) al
// (re)conectar el socket y al recuperar foco/visibilidad, y hace MERGE IDEMPOTENTE en el store.
//
// Este módulo es la lógica PURA del merge (extraída para poder testearla sin React ni red):
// decide qué filas de la respuesta deben inyectarse al store del Kanban.

import { Order } from '@interfaces/OrderTypes';
import { ORDER_STATUS_MAP } from '@utils/constants';

// Estatus que NO pertenecen al Kanban "Activos". Mismo predicado que el filtro de render de
// `Orders.tsx` (Activos): se excluyen scheduled / delivered / rejected / canceled / pre_checkout,
// y cualquier estatus desconocido (no presente en ORDER_STATUS_MAP). Mantener en sync con ese
// filtro: si el Kanban cambia qué considera activo, este set debe seguirlo.
const NON_ACTIVE_KANBAN_STATUSES: ReadonlySet<string> = new Set<string>([
  ORDER_STATUS_MAP.scheduled.key,
  ORDER_STATUS_MAP.delivered.key,
  ORDER_STATUS_MAP.rejected.key,
  ORDER_STATUS_MAP.canceled.key,
  ORDER_STATUS_MAP.pre_checkout.key,
]);

// ¿Este pedido pertenece al Kanban "Activos"?
//
// Es `true` solo si su `estatus` está en ORDER_STATUS_MAP y NO es uno de los terminales/no-activos.
// Un pedido aún `scheduled` (programado a futuro, todavía no activado por el backend) devuelve
// `false`: la reconciliación NUNCA inyecta programados que no se han activado al Kanban.
export const isActiveKanbanOrder = (order: Pick<Order, 'estatus'>): boolean => {
  const status = order.estatus;
  if (!status) return false;
  if (!(status in ORDER_STATUS_MAP)) return false;
  return !NON_ACTIVE_KANBAN_STATUSES.has(status);
};

// Dadas las filas recién traídas del backend y los pedidos ya presentes en el store, devuelve las
// que deben AÑADIRSE al Kanban: las que son activas (isActiveKanbanOrder) y aún no están en el store
// (dedup por `id`). El resultado se inyecta una a una con `addOrder`, que además vuelve a deduplicar
// en el reducer — doble red contra parpadeo/duplicados.
//
// No retorna actualizaciones de pedidos ya presentes: el path realtime (`useChangeOrderStatus`) ya
// mantiene el estatus al día. La reconciliación solo INYECTA lo que el push perdió, sin pisar el
// estado optimista que el operador pudo haber avanzado localmente.
export const selectOrdersToReconcile = (fetchedRows: Order[], storeOrders: Pick<Order, 'id'>[]): Order[] => {
  if (!Array.isArray(fetchedRows) || fetchedRows.length === 0) return [];
  const existingIds = new Set(storeOrders.map((o) => o.id));
  const seen = new Set<string>();
  const toAdd: Order[] = [];
  for (const row of fetchedRows) {
    if (!row || !row.id) continue;
    if (existingIds.has(row.id)) continue; // ya en el store → no duplicar
    if (seen.has(row.id)) continue; // dedup dentro de la misma respuesta
    if (!isActiveKanbanOrder(row)) continue; // no inyectar terminales ni scheduled
    seen.add(row.id);
    toAdd.push(row);
  }
  return toAdd;
};
